import React, { useState, useEffect } from 'react';
import ModalLayout from './ModalLayout';
import useDeviceManagement from '../../hooks/useDeviceManagement';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from '../../redux/modals';
import { setDeviceId, setState, setTriggers } from '../../redux/device';
import { getFirmware, getState, fetchDevices } from '../../api/devices';
import { setFirmware } from '../../redux/firmware';
import { fetchTriggers } from '../../api/trigger';

const availablePlatforms = ['ESP8266', 'ESP32'];

function DeviceCreationModal({ closeDeviceCreationModal }) {
  const dispatch = useDispatch();
  const [platform, setPlatform] = useState('ESP8266');
  const [fqbn, setFqbn] = useState('');
  const [nameError, setNameError] = useState('');
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [wifiError, setWifiError] = useState('');
  const [wifiSsidError, setWifiSsidError] = useState('');
  const [wifiPasswordError, setWifiPasswordError] = useState('');

  const availableFqbn = useSelector((state) => state.device.availableFqbn);
  const defaultFqbn = useSelector((state) => state.device.defaultFqbn);

  // Получаем информацию о необходимости MQTT для выбранного FQBN
  const selectedFqbnInfo = availableFqbn[platform]?.find(
    (item) => item.code === fqbn
  );
  const needMqtt = selectedFqbnInfo?.need_mqtt_in_build;

  useEffect(() => {
    setFqbn(defaultFqbn[platform]);
  }, [platform]);

  useEffect(() => {
    if (!needMqtt) {
      setWifiSsid('');
      setWifiPassword('');
      setWifiSsidError('');
      setWifiPasswordError('');
    }
  }, [selectedFqbnInfo]);

  const [name, setName] = useState('');
  const { addNewDevice } = useDeviceManagement();

  const validateWifiField = (value) => {
    return /^[\x20-\x7E]+$/.test(value);
  };

  const handleWifiSsidChange = (e) => {
    const value = e.target.value;
    setWifiSsid(value);

    if (value && !validateWifiField(value)) {
      setWifiSsidError(
        'Разрешены только латинские буквы, цифры и стандартные символы'
      );
    } else {
      setWifiSsidError('');
    }

    if (wifiError) setWifiError('');
  };

  const handleWifiPasswordChange = (e) => {
    const value = e.target.value;
    setWifiPassword(value);

    if (value && !validateWifiField(value)) {
      setWifiPasswordError(
        'Разрешены только латинские буквы, цифры и стандартные символы'
      );
    } else {
      setWifiPasswordError('');
    }

    if (wifiError) setWifiError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Проверка длины названия
    if (name.trim().length <= 1) {
      setNameError('Укажите название');
      return;
    }

    // Проверка полей Wi-Fi, если нужно MQTT
    if (needMqtt) {
      if (!wifiSsid.trim()) {
        setWifiError('Укажите SSID Wi-Fi сети');
        return;
      }
      if (!wifiPassword.trim()) {
        setWifiError('Укажите пароль Wi-Fi сети');
        return;
      }

      // Дополнительная проверка валидации символов
      if (!validateWifiField(wifiSsid)) {
        setWifiSsidError('SSID содержит недопустимые символы');
        return;
      }
      if (!validateWifiField(wifiPassword)) {
        setWifiPasswordError('Пароль содержит недопустимые символы');
        return;
      }
    }

    setNameError('');
    setWifiError('');
    setWifiSsidError('');
    setWifiPasswordError('');

    const deviceId = await addNewDevice(
      platform,
      name,
      fqbn,
      wifiSsid,
      wifiPassword
    );

    const devices = await fetchDevices();
    const device = devices.find((d) => d.id === deviceId);
    dispatch(setDeviceId(deviceId));
    dispatch(setState(device.state));
    const [firmware, state, triggers] = await Promise.all([
      getFirmware(deviceId),
      getState(deviceId),
      fetchTriggers(deviceId),
    ]);
    dispatch(setState(state));
    dispatch(setFirmware(firmware));
    dispatch(setTriggers(triggers));
    dispatch(openModal('deviceModal'));
    closeDeviceCreationModal();
  };

  return (
    <ModalLayout
      closeModal={closeDeviceCreationModal}
      className="!min-w-0 !min-h-0 w-full md:w-fit"
    >
      <div className="space-y-4 p-2">
        <div className="space-y-4">
          <div>
            <label className="block font-medium text-gray-700">Платформа</label>
            <div className="w-full mt-1 flex space-x-2">
              {availablePlatforms.map((unit) => (
                <button
                  key={unit}
                  onClick={() => setPlatform(unit)}
                  className={`px-4 py-2 w-full  font-medium focus:outline-none ${
                    platform === unit
                      ? 'bg-gray-100 border rounded border-blue-600 text-blue-900'
                      : 'bg-gray-100 border rounded border-gray-300 text-gray-500 hover:bg-gray-300'
                  }`}
                >
                  {unit}
                </button>
              ))}
            </div>
          </div>
          <div className="min-w-[20em]">
            <label className="block font-medium text-gray-700">
              FQBN (полное имя платы)
            </label>

            <select
              value={fqbn}
              onChange={(e) => setFqbn(e.target.value)}
              className="w-full border mt-1 border-blue-200 bg-white/70 text-blue-900 px-3 py-1 rounded-lg focus:outline-none"
            >
              {availableFqbn[platform].map((unit) => (
                <option
                  key={unit.code}
                  value={unit.code}
                  className={`${unit.code === defaultFqbn[platform] ? 'bg-blue-100' : ''}`}
                >
                  {unit.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="block font-medium text-gray-700">
            Название устройства <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError && e.target.value.trim().length > 1) {
                setNameError('');
              }
            }}
            className={`mt-1 block w-full rounded border px-3 py-2 focus:outline-none ${
              nameError
                ? 'border-red-500'
                : 'border-gray-200 focus:border-blue-500'
            }`}
            placeholder="Термометр"
          />
          {nameError && (
            <p className="mt-1 text-sm text-red-600">{nameError}</p>
          )}
        </div>

        {/* Поля для Wi-Fi */}
        {needMqtt && (
          <div className="space-y-3">
            <div>
              <label className="block font-medium text-gray-700">
                Wi-Fi SSID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={wifiSsid}
                onChange={handleWifiSsidChange}
                className={`mt-1 block w-full rounded border px-3 py-2 focus:outline-none ${
                  wifiSsidError
                    ? 'border-red-500'
                    : 'border-gray-200 focus:border-blue-500'
                }`}
                placeholder="Название Wi-Fi сети"
                required={needMqtt}
              />
              {wifiSsidError && (
                <p className="mt-1 text-sm text-red-600">{wifiSsidError}</p>
              )}
            </div>
            <div>
              <label className="block font-medium text-gray-700">
                Wi-Fi пароль <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={wifiPassword}
                onChange={handleWifiPasswordChange}
                className={`mt-1 block w-full rounded border px-3 py-2 focus:outline-none ${
                  wifiPasswordError
                    ? 'border-red-500'
                    : 'border-gray-200 focus:border-blue-500'
                }`}
                placeholder="Пароль от Wi-Fi"
                required={needMqtt}
              />
              {wifiPasswordError && (
                <p className="mt-1 text-sm text-red-600">{wifiPasswordError}</p>
              )}
            </div>
            {wifiError && (
              <p className="mt-1 text-sm text-red-600">{wifiError}</p>
            )}
            {needMqtt && (
              <p className="text-sm text-gray-500">
                * Поля обязательны — без них устройство не подключится.<br></br>
                Разрешены только латинские буквы, цифры и стандартные символы.
              </p>
            )}
          </div>
        )}

        <button
          onClick={handleSubmit}
          className="w-full py-2 text-lg font-medium border shadow-md"
        >
          Создать
        </button>
      </div>
    </ModalLayout>
  );
}

export default DeviceCreationModal;
